import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTitle } from "../hooks/useTitle";
import {
  AutoComplete,
  Button,
  Card,
  ColorPicker,
  Form,
  Input,
  Spin,
} from "antd";
import {
  useEditResourceConfig,
  useGetResourceConfig,
} from "../queries/resource";
import {
  DEFAULT_BOOKED_COLOR,
  DEFAULT_FREE_COLOR,
} from "../constants/color-constants";
import { EditResourceFormValues } from "../types/resource";
import { AggregationColor } from "antd/es/color-picker/color";
import { DEFAULT_LOCALES } from "../constants/defaultLocale-constants";

const EditResourcePage = () => {
  const { t } = useTranslation();
  useTitle(t("page-title.edit-resource"));
  const { id } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const {
    data: resourceConfig,
    isSuccess,
    isFetching,
  } = useGetResourceConfig(id || "");
  const { mutate: editResource, isPending } = useEditResourceConfig();

  const onFinish = (values: EditResourceFormValues) => {
    editResource({
      resourceId: id || "",
      resourceName: values.resourceName,
      locale: values.locale,
      colors: {
        freeColor: values.freeColor?.toHexString(),
        bookedColor: values.bookedColor?.toHexString(),
      },
    });
  };

  useEffect(() => {
    if (isSuccess && resourceConfig) {
      form.setFieldsValue({
        resourceName: resourceConfig.resourceName,
        locale: resourceConfig.locale,
        freeColor: new AggregationColor(
          resourceConfig.colors?.freeColor || DEFAULT_FREE_COLOR,
        ),
        bookedColor: new AggregationColor(
          resourceConfig.colors?.bookedColor || DEFAULT_BOOKED_COLOR,
        ),
      });
    }
  }, [resourceConfig, isSuccess]);

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 500,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        marginTop: "2.5rem",
      }}
    >
      <Spin spinning={isFetching} tip={t("loading")}>
        <Card
          title={t("pages.edit-resource.edit-resource")}
          style={{ width: 500 }}
        >
          <Form layout="vertical" onFinish={onFinish} form={form}>
            <Form.Item
              label={t("pages.edit-resource.name")}
              name="resourceName"
              rules={[
                {
                  required: true,
                  message:
                    t("pages.edit-resource.name-required") ||
                    "Name is required",
                },
              ]}
            >
              <Input
                placeholder={t("pages.edit-resource.name") || undefined}
                maxLength={1000}
              />
            </Form.Item>
            <Form.Item
              label={t("pages.edit-resource.locale")}
              name="locale"
              rules={[
                {
                  required: true,
                  message:
                    t("pages.edit-resource.locale-required") ||
                    "Locale is required",
                },
                {
                  validator: async (_, value) => {
                    if (!Intl.DateTimeFormat.supportedLocalesOf(value).length) {
                      return Promise.reject(
                        new Error(
                          t("pages.edit-resource.invalid-locale") ||
                            "Invalid locale",
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <AutoComplete
                showSearch
                placeholder={"en-US"}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={DEFAULT_LOCALES}
              />
            </Form.Item>
            <div style={{ display: "flex" }}>
              <Form.Item
                label={t("pages.edit-resource.free-color")}
                name="freeColor"
                rules={[
                  {
                    required: true,
                    message:
                      t("pages.edit-resource.color-required") ||
                      "A color is required",
                  },
                ]}
                style={{ marginRight: "2rem" }}
              >
                <ColorPicker
                  showText={(color) => <span>{color.toHexString()}</span>}
                  onChange={(color) => color.toHexString()}
                  style={{ float: "left" }}
                />
              </Form.Item>
              <Form.Item
                label={t("pages.edit-resource.booked-color")}
                name="bookedColor"
                rules={[
                  {
                    required: true,
                    message:
                      t("pages.edit-resource.color-required") ||
                      "A color is required",
                  },
                ]}
              >
                <ColorPicker
                  showText={(color) => <span>{color.toHexString()}</span>}
                  style={{ float: "left" }}
                />
              </Form.Item>
            </div>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                style={{ float: "right" }}
                type="primary"
                htmlType="submit"
                loading={isPending}
              >
                {t("save")}
              </Button>
            </Form.Item>
          </Form>
        </Card>
        <Button
          onClick={() => navigate("/portal/resources")}
          style={{
            marginTop: "1rem",
            float: "left",
          }}
        >
          {t("back")}
        </Button>
      </Spin>
    </div>
  );
};

export default EditResourcePage;
